import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const http = axios.create({ baseURL: API, withCredentials: true });
const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};
const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { const { data } = await http.post("/login", { email, password }); onLogin(data); }
    catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="admin-login-wrap" data-testid="admin-login-page">
      <form className="admin-login-card" onSubmit={submit} data-testid="admin-login-form">
        <span className="eyebrow">Players Health · Admin</span>
        <h1 className="display display-m admin-login-title">Guidebook Leads</h1>
        <div className="form-row"><label htmlFor="ad-email">Email</label><input id="ad-email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" /></div>
        <div className="form-row"><label htmlFor="ad-pass">Password</label><input id="ad-pass" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" /></div>
        <button type="submit" className="btn btn-accent" style={{ width: "100%" }} disabled={busy} data-testid="admin-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
        {error && <p className="gate-error" role="alert" data-testid="admin-login-error">{error}</p>}
      </form>
    </div>
  );
};

const StatCard = ({ label, value, testid }) => (
  <div className="admin-stat" data-testid={testid}><span>{label}</span><strong>{value}</strong></div>
);

const LeadsTable = ({ leads }) => (
  <div className="admin-table-wrap">
    <table className="admin-table" data-testid="admin-leads-table">
      <thead><tr><th>Date</th><th>First name</th><th>Email</th><th>Organization</th><th>Guidebook</th><th>Type</th></tr></thead>
      <tbody>
        {leads.length === 0 ? (
          <tr><td colSpan={6} className="admin-empty" data-testid="admin-leads-empty">No leads yet.</td></tr>
        ) : leads.map((l) => (
          <tr key={l.id} data-testid={`admin-lead-row-${l.id}`}>
            <td>{fmtDate(l.created_at)}</td><td>{l.first_name}</td><td><a href={`mailto:${l.email}`}>{l.email}</a></td>
            <td>{l.organization || "—"}</td><td>{l.guidebook_title}</td>
            <td><span className={`admin-pill${l.returning ? " is-returning" : ""}`}>{l.returning ? "Returning" : "New"}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Dashboard = ({ admin, onLogout }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [gid, setGid] = useState("");
  const load = useCallback(async () => {
    try { const res = await http.get("/guidebook-leads", { params: { q, guidebook_id: gid } }); setData(res.data); }
    catch (e) { if (e?.response?.status === 401) onLogout(); }
  }, [q, gid, onLogout]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const exportUrl = (fmt) => `${API}/guidebook-leads/export?${new URLSearchParams({ format: fmt, q, guidebook_id: gid })}`;
  const logout = async () => { await http.post("/logout").catch(() => {}); onLogout(); };
  return (
    <div className="admin-dash" data-testid="admin-dashboard">
      <header className="admin-header">
        <div><span className="eyebrow">Players Health · Admin</span><h1 className="display display-m">Guidebook Leads</h1></div>
        <div className="admin-header-actions">
          <span className="admin-user" data-testid="admin-user-email">{admin.email}</span>
          <button type="button" className="btn btn-ghost-dark" onClick={logout} data-testid="admin-logout-btn">Log out</button>
        </div>
      </header>
      <div className="admin-stats">
        <StatCard label="Total downloads" value={data?.stats.total ?? "—"} testid="admin-stat-total" />
        <StatCard label="Unique emails" value={data?.stats.unique_emails ?? "—"} testid="admin-stat-unique" />
        <StatCard label="Last 7 days" value={data?.stats.last_7_days ?? "—"} testid="admin-stat-week" />
      </div>
      <div className="admin-toolbar">
        <input className="admin-search" placeholder="Search name, email, organization" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" />
        <select className="admin-select" value={gid} onChange={(e) => setGid(e.target.value)} data-testid="admin-guidebook-filter">
          <option value="">All guidebooks</option>
          {data?.guidebooks.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
        </select>
        <a className="btn btn-accent" href={exportUrl("csv")} data-testid="admin-export-csv">Export CSV</a>
        <a className="btn btn-primary on-light" href={exportUrl("xlsx")} data-testid="admin-export-xlsx">Export Excel</a>
      </div>
      {data ? <LeadsTable leads={data.leads} /> : <p className="admin-empty" data-testid="admin-loading">Loading…</p>}
    </div>
  );
};

export default function AdminLeadsPage() {
  const [admin, setAdmin] = useState(null);
  useEffect(() => {
    http.get("/me").then((r) => setAdmin(r.data)).catch(() => setAdmin(false));
  }, []);
  const logout = useCallback(() => setAdmin(false), []);
  if (admin === null) return <p className="admin-empty" data-testid="admin-checking">Checking session…</p>;
  return admin ? <Dashboard admin={admin} onLogout={logout} /> : <AdminLogin onLogin={setAdmin} />;
}
